import { useCallback, useEffect, useRef } from "react";
import { useVerifyMobileMoneyAccount } from "../../../../hooks/queries/useMerchantQueries";
import useManualEntryStore from "../../../../stores/manualEntryStore";
import { PHONE_VERIFICATION_STATUS } from "../config/verificationStatus";
import { CHANNEL_TO_PROVIDER } from "../config/manualEntryConfig";
import { extractAxiosErrorMessage } from "../../../../services/utils/errorUtils";

const VALID_PHONE_RE = /^254[71]\d{8}$/;

export function usePhoneVerification({ paymentChannel, phoneValue, setValue }) {
  const phoneVerificationStatus = useManualEntryStore(
    (s) => s.phoneVerificationStatus,
  );

  const { mutate, isPending: isVerifying } = useVerifyMobileMoneyAccount();
  const pendingPhoneRef = useRef(null);
  const prevChannelRef = useRef(undefined);

  const phoneValueRef = useRef(phoneValue);
  phoneValueRef.current = phoneValue;

  const verify = useCallback(
    (phone) => {
      const provider = CHANNEL_TO_PROVIDER[paymentChannel];
      if (!provider) return;
      pendingPhoneRef.current = phone;

      mutate(
        { msisdn: phone, provider },
        {
          onSuccess: (data) => {
            if (pendingPhoneRef.current !== phone) return;
            const store = useManualEntryStore.getState();

            if (data.success) {
              store.setPhoneVerification({
                status: PHONE_VERIFICATION_STATUS.VERIFIED,
                phone,
                name: data.recipientName || null,
                message: null,
              });
              setValue("recipientName", data.recipientName || "");
            } else {
              store.setPhoneVerification({
                status: PHONE_VERIFICATION_STATUS.UNVERIFIED,
                phone,
                name: null,
                message: data.message || "Verification failed",
              });
              setValue("recipientName", "");
            }
          },
          onError: (error) => {
            if (pendingPhoneRef.current !== phone) return;
            const message = extractAxiosErrorMessage(
              error,
              "Could not verify recipient",
            );
            useManualEntryStore.getState().setPhoneVerification({
              status: PHONE_VERIFICATION_STATUS.UNVERIFIED,
              phone,
              name: null,
              message,
            });
            setValue("recipientName", "");
          },
        },
      );
    },
    [paymentChannel, mutate, setValue],
  );

  useEffect(() => {
    const prevChannel = prevChannelRef.current;
    prevChannelRef.current = paymentChannel;

    if (prevChannel === undefined || prevChannel === paymentChannel) return;

    useManualEntryStore.getState().resetPhoneVerification();
    setValue("recipientName", "");

    if (paymentChannel && VALID_PHONE_RE.test(phoneValueRef.current)) {
      verify(phoneValueRef.current);
    }
  }, [paymentChannel, verify, setValue]);

  const handlePhoneChange = useCallback(
    (value) => {
      const { verifiedPhone, phoneVerificationStatus: status } =
        useManualEntryStore.getState();
      if (
        value !== verifiedPhone &&
        status !== PHONE_VERIFICATION_STATUS.IDLE
      ) {
        useManualEntryStore.getState().resetPhoneVerification();
        setValue("recipientName", "");
      }
    },
    [setValue],
  );

  const handlePhoneBlur = useCallback(
    (value) => {
      if (!paymentChannel) return;
      const { verifiedPhone } = useManualEntryStore.getState();
      if (value === verifiedPhone) return;
      if (VALID_PHONE_RE.test(value)) {
        verify(value);
      }
    },
    [paymentChannel, verify],
  );

  const isVerified =
    phoneVerificationStatus === PHONE_VERIFICATION_STATUS.VERIFIED;
  const isUnverified =
    phoneVerificationStatus === PHONE_VERIFICATION_STATUS.UNVERIFIED;

  return {
    handlePhoneChange,
    handlePhoneBlur,
    isVerifying,
    isVerified,
    isUnverified,
  };
}
